/**
 * Figma: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=2741-99515&m=dev
 *
 * Back navigation for the order details page.
 *
 * - Mobile (<768px): icon-only back arrow (unchanged).
 * - Tablet/desktop (>=768px): "Orders / Order Details" breadcrumb. The "Orders"
 *   crumb triggers the same `onBack` callback (navigation is state/callback-based,
 *   not URL-based), so it resolves to the order list regardless of how the details
 *   page was reached. "Order Details" is a static, non-interactive current-page label.
 */

import { Icon } from '@susu/ui/components'

import { useLabel } from '../../../i18n'

interface NavigationProps {
  onBack?: () => void
}

export default function Navigation({ onBack }: NavigationProps) {
  const t = useLabel()

  const handleBack = () => {
    onBack?.()
  }

  return (
    <div className="flex items-start max-lg:pt-32 pb-16">
      {/* Mobile: icon-only back arrow */}
      <button
        onClick={handleBack}
        className="md:hidden flex relative items-center justify-center transition-opacity hover:opacity-80"
        aria-label={t('orderList.navigation.goBack')}
        type="button"
      >
        <Icon name="arrow-back" size="medium" className="h-20" />
        <span className="absolute w-44 h-44 inset-0 -translate-1/4" />
      </button>

      {/* Tablet/desktop: breadcrumb */}
      <nav
        aria-label={t('orderList.navigation.breadcrumb')}
        className="hidden md:block"
        data-testid="order-details-breadcrumb"
      >
        <ol className="flex items-center gap-8 text-sm leading-sm text-text">
          <li>
            <button
              onClick={handleBack}
              className="font-light transition-opacity hover:opacity-80"
              type="button"
              data-testid="order-details-breadcrumb-orders"
            >
              {t('orderList.navigation.orders')}
            </button>
          </li>
          <li aria-hidden="true" className="font-light text-text-secondary">
            /
          </li>
          <li>
            <span aria-current="page" className="font-medium">
              {t('orderList.navigation.orderDetails')}
            </span>
          </li>
        </ol>
      </nav>
    </div>
  )
}
